/**
 * Redirects legacy `/member/*` URLs onto the chapter-scoped
 * `/c/:chapterSlug/m/*` path. The chosen chapter is the user's first
 * direct chapter membership; institution admins (with no direct chapter
 * row) default to the institution's defaultChapterSlug, or the first
 * chapter, or `96` as a last resort.
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useMyMemberships } from '@/hooks/useCan';
import { chaptersApi, type ChapterSummary } from '@/services/api';

export function MemberRedirect() {
  const { memberships, loading } = useMyMemberships();
  const [chapters, setChapters] = useState<ChapterSummary[] | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (loading) return;
    chaptersApi.list('navarasam').then(setChapters).catch(() => setChapters([]));
  }, [loading]);

  if (loading || chapters === null) {
    return <div className="container mx-auto px-4 py-12 text-white/60">Loading…</div>;
  }

  // Strip leading "/member" so we can re-attach the chapter prefix.
  const tail = location.pathname.replace(/^\/member/, '') || '/dashboard';

  const directChapter = memberships.find((m) => m.scopeType === 'chapter');
  const isInstitutionAdmin = memberships.some(
    (m) => m.scopeType === 'institution' && m.role === 'admin',
  );

  let slug: string | undefined;
  if (directChapter) {
    slug = chapters.find((c) => c.id === directChapter.scopeId)?.slug;
  }
  if (!slug && isInstitutionAdmin) {
    slug = chapters[0]?.slug;
  }
  if (!slug) {
    // No memberships at all — let the protected route surface the
    // "no chapter" message via /c/96 fallback.
    slug = chapters[0]?.slug ?? '96';
  }

  return <Navigate to={`/c/${slug}/m${tail}`} replace />;
}

export default MemberRedirect;
